import { useMemo } from 'react';
import type {
  ProjectWorkspaceInfoFragment,
  Workspace,
} from '@frameio/graphql-client/generated';
import {
  ProjectTree,
  type ProjectTreeItem,
  type WindowedProjectTree,
} from '@frameio/project-tree';
import {
  ProjectHeaderBackButton,
  ProjectHeaderStatic,
} from '@frameio/project-tree/components/ProjectHeader';
import {
  useCachedEntities,
  useProjectFolders,
} from '@frameio/project-tree/hooks';
import { LoadingContainer } from '@frameio/vapor-migration';
import styles from './FoldersView.module.css';
import TEXT from './generated/TEXT/ExportFoldersView';

const FOLDER_TREE_PADDING_X = 16;
const FOLDER_TREE_PADDING_Y = 8;

TEXT.DEFINE({
  /** Error message displayed when there is an issue retrieving the Project */
  errorGettingProject: 'An error has occurred retrieving the Project',
});

type FoldersViewProps = {
  disableBack: boolean;
  onBackSelect: () => void;
  onFolderSelect: (folderId: string) => void;
  project?: ProjectWorkspaceInfoFragment;
  targetFolderId?: string;
  workspace?: Pick<Workspace, 'id' | 'name'>;
};

export function FoldersView({
  disableBack,
  onBackSelect,
  onFolderSelect,
  project: projectProp,
  targetFolderId,
  workspace: workspaceProp,
}: FoldersViewProps) {
  const { project, workspace } = useCachedEntities<
    Pick<FoldersViewProps, 'project' | 'workspace'>
  >({
    project: projectProp,
    workspace: workspaceProp,
  });

  const { error, fetchMoreFolders, isLoading, toggleFolder, tree } =
    useProjectFolders(project?.rootAssetId, true);

  const wrappedTree = useMemo<WindowedProjectTree>(
    () => ({
      children: [tree],
      isOpen: true,
      props: { id: 'hidden-root', type: 'hidden-root' },
    }),
    [tree]
  );

  const selectedItem = useMemo<ProjectTreeItem>(
    () => ({
      id: targetFolderId ?? '',
      type: targetFolderId === project?.rootAssetId ? 'root-folder' : 'folder',
    }),
    [project?.rootAssetId, targetFolderId]
  );

  if (!project) return null;

  const header = disableBack ? (
    <ProjectHeaderStatic
      avatarUrl={project.images?.image32 ?? ''}
      details={workspace?.name ?? ''}
      isPrivate={project.private}
      projectId={project.id}
      projectName={project.name}
    />
  ) : (
    <ProjectHeaderBackButton
      avatarUrl={project.images?.image32 ?? ''}
      details={workspace?.name ?? ''}
      isPrivate={project.private}
      onSelect={onBackSelect}
      projectId={project.id}
      projectName={project.name}
    />
  );

  const handleItemSelect = (item: ProjectTreeItem) => {
    onFolderSelect(item.id);
  };

  return (
    <div className={styles.grid}>
      {header}
      <LoadingContainer
        key={project?.id}
        errorMessage={error ? TEXT.errorGettingProject : undefined} // force remount when project changes
        isLoading={isLoading}
        spinnerDelay={0}
        style={{ overflow: 'hidden' }}
      >
        {!isLoading && (
          <ProjectTree
            disableContextMenus={true}
            dndNamespace={null}
            onBoundarySeen={fetchMoreFolders}
            onItemSelect={handleItemSelect}
            paddingX={FOLDER_TREE_PADDING_X}
            paddingY={FOLDER_TREE_PADDING_Y}
            rootFolderId={project.rootAssetId}
            selectedItem={selectedItem}
            toggleItem={toggleFolder}
            tree={wrappedTree}
          />
        )}
      </LoadingContainer>
    </div>
  );
}
